import {
  IndexedDBDocStorage,
  IndexedDBDocSyncStorage,
} from '@affine/nbstore/idb';
import { SqliteDocStorage, SqliteDocSyncStorage } from '@affine/nbstore/sqlite';
import type {
  StoreClient,
  WorkerInitOptions,
} from '@affine/nbstore/worker/client';
import { Entity } from '@toeverything/infra';

import type { ServerService } from '../../cloud';
import type { NbstoreService } from '../../storage';

/**
 * Store options for the per-user settings database (editor settings and similar).
 *
 * PIPE keeps this database on the device only. Upstream also registered a `CloudDocStorage`
 * remote here, which opens AFFiNE's socket.io connection to the server. PIPE's sync server does
 * not speak socket.io, so that remote never synced anything; it only retried a failing websocket
 * every few seconds in the storage worker.
 */
export function userDBStoreOptions({
  userId,
  serverId,
  docStorage,
  docSyncStorage,
}: {
  userId: string;
  serverId: string;
  docStorage: string;
  docSyncStorage: string;
}): WorkerInitOptions {
  const opts = { type: 'userspace', flavour: serverId, id: userId } as const;
  return {
    local: {
      doc: { name: docStorage, opts },
      docSync: { name: docSyncStorage, opts },
    },
    remotes: {},
  };
}

export class UserDBEngine extends Entity<{
  userId: string;
}> {
  private readonly userId = this.props.userId;
  readonly client: StoreClient;

  DocStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteDocStorage
      : IndexedDBDocStorage;
  DocSyncStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteDocSyncStorage
      : IndexedDBDocSyncStorage;

  canGracefulStop() {
    // TODO(@eyhn): Implement this
    return true;
  }

  constructor(
    private readonly nbstoreService: NbstoreService,
    serverService: ServerService
  ) {
    super();

    const { store, dispose } = this.nbstoreService.openStore(
      `userspace:${serverService.server.id},${this.userId}`,
      userDBStoreOptions({
        userId: this.userId,
        serverId: serverService.server.id,
        docStorage: this.DocStorageType.identifier,
        docSyncStorage: this.DocSyncStorageType.identifier,
      })
    );
    this.client = store;
    this.client.docFrontend.start();
    this.disposables.push(() => dispose());
  }
}
